import React, { useState, useEffect, useContext } from "react";
import { Page, Card, Layout, PageActions, Banner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import FieldsForm from "./Form";
import { Navigate } from "react-router-dom";
import { AppContext } from "../../../../components/global/appContext";
import { useParams } from "react-router-dom";
import { TitleBar, ContextualSaveBar } from "@shopify/app-bridge-react";
import useCompare from "../../../../hooks/useCompare";
import FieldSkeletonForm from "./SkeletonForm";

import {
  useGetField,
  useUpdateField,
  useDeleteField,
} from "../../../../hooks/queries/fields";

export default function FieldsEdit(props) {
  const { t } = useTranslation();
  const appContext = useContext(AppContext);
  let { id } = useParams();

  const [redirect, setRedirect] = useState(false);

  const { data, isError } = useGetField(id);
  const [field, setField] = useState(data);
  useEffect(() => setField(data), [data]);

  const updateFieldMutation = useUpdateField(id, {
    onSuccess: () => appContext.setMessage(t("settings.customFields.messages.savedSuccessfully")),
    onError: () => appContext.setMessage(t("settings.customFields.messages.couldntSave"), true),
  });

  const deleteFieldMutation = useDeleteField(id, {
    onSuccess: () => {
      setRedirect(true);
      appContext.setMessage(t("settings.customFields.messages.deletedSuccessfully"));
    },
    onError: () => appContext.setMessage(t("settings.customFields.messages.couldntDelete"), true),
  });

  const hasUnsavedChanges = !useCompare(data, field);

  if (redirect)
    return (
      <>
        <ContextualSaveBar visible={false} leaveConfirmationDisable />
        <Navigate to="/settings/fields" />
      </>
    );
  if (isError) return <p>{t("settings.customFields.error")}</p>;
  if (!field) return <FieldSkeletonForm />;

  const isLocalizationEnabled = field?.localization_enabled;

  return (
    <Page>
      <TitleBar
        title={field.name}
        breadcrumbs={[
          { content: t("settings.common.breadcrumbs.settings") },
          { content: t("settings.customFields.title"), url: "/settings/fields" },
        ]}
      />
      <ContextualSaveBar
        fullWidth
        visible={hasUnsavedChanges}
        saveAction={{
          onAction: () => updateFieldMutation.mutate(field),
          loading: updateFieldMutation.isLoading,
        }}
        discardAction={{
          onAction: () => setField(data),
        }}
      />
      <Layout>
        <Layout.Section>
          <Card sectioned>
            <FieldsForm field={field} setField={setField} />
          </Card>
        </Layout.Section>
        <Layout.Section>
          <PageActions
            secondaryActions={[
              {
                content: t("settings.customFields.actions.delete"),
                onAction: deleteFieldMutation.mutate,
                loading: deleteFieldMutation.isLoading,
                destructive: true,
              },
            ]}
          />
        </Layout.Section>
      </Layout>
    </Page>
  );
}
